import * as React from 'react';

import { useDispatch } from 'react-redux';
import { OrderContext } from './OrderProvider';
import { DispensaryContext } from '../dispensary/DispensaryProvider';
import { setDispensary, resetCart, addToCart } from '../actions/cartActions';
import BodyWrapper, { LayoutTypes } from '../layout/BodyWrapper';
import ReOrderHeader from './ReOrderHeader';
import ReOrderPickupButton from './ReOrderPickupButton';
import ReOrderItem from './ReOrderItem';
import PrimaryHeader from '../styleguide/PrimaryHeader';
import Spinner from '../common/Spinner';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';

import styles from './ReOrder.module.css';

/**
 * Display prior order details for use in reordering
 */
const ReOrderDetails = ({
  orderId
}) => {
 
  // Get the user's order history  
  const { recentOrders } = React.useContext(OrderContext);
  // The order we are reordering
  const [priorOrder, setPriorOrder] = React.useState({});
  // The order item map ( to handle quantity changes )
  const [orderItemMap, setOrderItemMap] = React.useState(new Map());
  const updateItemMap = (id, itemInfo) => {
    setOrderItemMap(orderItemMap => new Map(orderItemMap.set(id, itemInfo))); 
  };
  // Total reorder price
  const [totalPrice, setTotalPrice] = React.useState(0);
  // Total flower grams
  const [totalFlowerGrams, setTotalFlowerGrams] = React.useState(0); 

  const [orderDate, setOrderDate] = React.useState();

  // The dispensary we're reordering from
  const { dispensary, setDispensaryId } = React.useContext(DispensaryContext);
  // Update dispensary in redux
  const dispatch = useDispatch(); 
  
  // Trigger AddToCart
  const [cartItemsNow, setCartItemsNow] = React.useState(false);
  
  // Add order info + dispensary to state
  React.useEffect(() => {
    if (recentOrders?.length) {
      const requestedOrder = recentOrders.find(order => order.id === orderId);
      if (requestedOrder) {
        setPriorOrder(requestedOrder);
        // Update DispensaryProvider if needed
        if ( dispensary?.id !== requestedOrder.dispensary_id) {
          setDispensaryId(requestedOrder.dispensary_id);
        }
        // Create item map to keep track of quantity changes
        if (orderItemMap.size === 0) {
          requestedOrder.items.forEach((item) => {
            orderItemMap.set(item.id,
              {
                price: item.display_info.cost_usa_cents,
                flower_equivalent_grams: item.flower_equivalent_grams,
                quantity: item.quantity_requested  
              });
          });
        }

        // Set the order date if not defined 
        if (!orderDate) {
          const rawDate = new Date(requestedOrder.created_at * 1000).toDateString();  
          const dateItems = rawDate.split(' ');
          setOrderDate(`${dateItems[1]} ${dateItems[2]}, ${dateItems[3]}`); 
        }

        // Keep total price updated
        const updatedPrice = Array.from(orderItemMap.values()).reduce((total, item) => {
          total+= item.price * item.quantity;
           return total;
        }, 0);
        setTotalPrice(updatedPrice);
        
        // Keep the flower grams updated
        const updatedFlowerGrams = Array.from(orderItemMap.values()).reduce((total, item) => {
          total+= Math.round(item.flower_equivalent_grams * item.quantity * 100)/100;
           return total;
        }, 0);
        setTotalFlowerGrams(updatedFlowerGrams);

      }
    }
  }, [dispensary, orderId, orderDate, recentOrders, setDispensaryId, orderItemMap, totalPrice]);
  
  return (
    /* NOTE: padding here is reduced via negative margin for mobile in CSS ---if changing, update both places */
    <BodyWrapper withStyle={{padding:'2rem 0 4rem',backgroundColor:'#eee'}} pageLayout={LayoutTypes.FullBleed}>
      <Helmet>
        <title>Zip Run - ReOrder</title>
      </Helmet>
      <div className={styles.reorderPage}>
        <div className={styles.pageHeaderWrap}>
          <div className={styles.pageHeader}>
            <PrimaryHeader text="Reorder" isCentered withStyle={{marginBottom:0}}/>
            { orderDate &&
              <div className={styles.headerNote}>
                This is your <b>{priorOrder.type}</b> order from <b>{orderDate}</b>. 
                If needed, adjust product quantities, remove products, and then
                proceed to checkout!
              </div>  
            }
          </div>
        </div>
      { priorOrder.id && dispensary?.id
        ? <div className={styles.reorderWrap}>
            <ReOrderHeader
              dispensaryId={dispensary.id}
              dispensaryName={dispensary.name}
              image_urls={dispensary.image_urls}
              flowerGrams={totalFlowerGrams} 
            />
            <ul className={styles.reorderItems}>
              { priorOrder.items.map((item) => {
                const {name, image_url, category, cost_usa_cents } = item.display_info;
                return <ReOrderItem
                  key={item.id}
                  Id={item.id}
                  dispensaryId={priorOrder.dispensary_id} 
                  name={name}
                  image_url={image_url}
                  category={category}
                  cost_usa_cents={cost_usa_cents}
                  quantity={item.quantity_requested}
                  leaf_logix_pricing_tier={item.leaf_logix_pricing_tier} 
                  productFlowerGrams={item.flower_equivalent_grams} 
                  totalFlowerGrams={totalFlowerGrams}
                  handleQuantityChange={updateItemMap} 
                  cartThisItem={cartItemsNow} 
                  addToCart={(quantity) => {
                    dispatch(addToCart(item.id, item, quantity));
                  }}/>
              })} 
            </ul>
      
            { totalPrice > 0 &&
              <ReOrderPickupButton
                itemTotal={totalPrice}
                onCheckoutClick={()=>{
                  dispatch(setDispensary(priorOrder.dispensary_id));
                  // We'll clear their cart now
                  dispatch(resetCart())
                  // Trigger adding the reorder items
                  setCartItemsNow(true);
                }} />   
            }
          </div>
        : <Spinner />
      }
      </div> 
    </BodyWrapper> 
  )
}

ReOrderDetails.propTypes = {
  orderId: PropTypes.string /* via router, undefined initially */
};

export default ReOrderDetails;